import { useEffect, useRef, useState } from 'preact/hooks';
import { read } from '../api/client.js';
import { WriteBoundary } from '../api/write-boundary.js';
import { taskResourceKey, useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Button, ButtonLink, Notice } from '../ui/primitives.jsx';
import { Resource } from '../ui/states.jsx';
import { ClosedHandoffRow, HandoffRow, ReferenceAction } from './TaskDetailPane.jsx';
import { ProposalDecision } from './ProposalDecision.jsx';
import { QuestionDecision } from './QuestionDecision.jsx';
import { inboxDecision } from './inbox-decision.js';
import { clearScope, draftScope, isAccessDenied } from './task-drafts.js';

// 선택한 사건 하나만 최신 Context Pack으로 읽는다. Inbox 저장 형식은 포인터 그대로다.
export function InboxDecision({ pointer, spaceId, home, archived, close, closeLabel = '목록으로' }) {
  const session = useSession();
  const heading = useRef(null);
  const [accepted, setAccepted] = useState(false);
  const [returning, openReturn] = useState(null);
  const scope = draftScope({ spaceId, taskId: pointer.taskId, nodeId: session.nodeId });
  const pack = useResource(taskResourceKey(spaceId, pointer.taskId), (signal) =>
    read('task-context-pack', { nodeId: session.nodeId, query: { spaceId, taskId: pointer.taskId }, signal }), { quiet: true, identity: session.nodeId });
  const assets = useResource(`space:${spaceId}:resources`, (signal) =>
    read('resources', { nodeId: session.nodeId, query: { spaceId }, signal }));
  useEffect(() => { heading.current?.focus(); return () => clearScope(scope); }, [scope]);
  useEffect(() => {
    if (pack.status === 'error' && isAccessDenied(pack.error)) { clearScope(scope); openReturn(null); }
  }, [pack.status, pack.error, scope]);
  const name = (id) => id === session.nodeId ? '나' : home?.members?.find((item) => item.nodeId === id)?.displayName ?? '이름 없는 자리';
  return <WriteBoundary resources={[pack]}><section class="inbox-decision" aria-labelledby="inbox-decision-title">
    <header class="inbox-decision__head">
      <h2 id="inbox-decision-title" ref={heading} tabIndex={-1} class="t-body-strong">요청 확인</h2>
      <Button size="sm" tone="ghost" onClick={close}>{closeLabel}</Button>
    </header>
    <Resource resource={pack} loadingLabel="현재 판단 상태를 확인하는 중">{(data) => {
      const decision = inboxDecision(pointer, data);
      const writable = !archived && ['owner', 'participant'].includes(data.task.viewerRole);
      return <div class="stack">
        <div class="row row--between">
          <p class="t-body-strong">{data.task.title}</p>
          <ButtonLink size="sm" tone="ghost" href={`/s/${spaceId}/task/${pointer.taskId}`}>Task 전체 보기</ButtonLink>
        </div>
        {accepted ? <p role="status" class="t-meta">변경에 동의한 판단이 기록됐습니다.</p> : null}
        {decision.kind === 'question' ? <QuestionDecision key={decision.question.questionId}
          question={decision.question} spaceId={spaceId} writable={writable} name={name} /> : null}
        {decision.kind === 'proposal' ? <ProposalDecision
          proposal={decision.proposal} task={data.task} spaceId={spaceId} mine={session.nodeId}
          name={name} archived={!writable} onAccepted={() => { setAccepted(true); heading.current?.focus(); }}
          evidence={<details class="inbox-evidence"><summary class="t-meta">근거 {decision.proposal.resolvedContext?.referenceIds?.length ?? 0}건 보기</summary>
            <ul class="handoff-package__list">{(decision.proposal.resolvedContext?.referenceIds ?? []).map((id) => {
              const reference = data.references?.find((item) => item.referenceId === id);
              return <li key={id}>{reference ? <>
                <p class="t-meta">{reference.kind === 'asset' ? '자료' : '대화'} · {reference.mode === 'snapshot' ? '고정된 판' : '최신 참조'}</p>
                <ReferenceAction item={reference} spaceId={spaceId} assets={assets} />
              </> : <p class="t-meta">이 근거를 현재 Task에서 읽을 수 없습니다.</p>}</li>;
            })}</ul>
          </details>}
        /> : null}
        {decision.kind === 'handoff' ? ['offered', 'accepted'].includes(decision.handoff.state)
          ? <HandoffRow handoff={decision.handoff} spaceId={spaceId} mine={session.nodeId} name={name}
            assets={assets} pack={data} writable={writable} onResponded={() => heading.current?.focus()}
            workspace={{ scope, returning, openReturn }} />
          : <ClosedHandoffRow handoff={decision.handoff} spaceId={spaceId} name={name} assets={assets} pack={data} /> : null}
        {decision.kind === 'promoted' ? <Notice title={`이 제안이 공동 이해 v${decision.version.versionNo}에 반영됐습니다.`} /> : null}
        {decision.kind === 'superseded' ? <Notice title="다른 공동 이해로 대체된 제안입니다. 다시 확정할 수 없습니다." /> : null}
        {decision.kind === 'unavailable' ? <Notice title="이 사건의 현재 내용은 Task 전체에서 확인해 주세요." /> : null}
      </div>;
    }}</Resource>
    <Button size="sm" tone="ghost" onClick={pack.reload}>최신 상태 확인</Button>
  </section></WriteBoundary>;
}
